import {Component, computed, inject, OnInit, signal} from '@angular/core';
import {GrafanaEmbedComponent} from '../../grafana/grafana-embed';
import {CommonModule, DatePipe} from '@angular/common';
import {VigimareIndicationService} from '../../services/vigimare-indication.service';
import {VigimareIndication} from '../../models/vigimare-indication.model';
import {VigimareAlertService} from '../../services/vigimare-alert.service';
import {AlertAuditEntry, AlertFeedback, AlertStatus, VigimareAlert} from '../../models/vigimare-alert.model';
import {VigimareVesselResponse} from '../../models/vigimare-vessel.model';
import {VigimareObjectResponse} from '../../models/vigimare-object.model';
import {Subscription} from 'rxjs';
import {ActivatedRoute} from '@angular/router';
import {KeycloakService} from '../../security/keycloak.service';

@Component({
  selector: 'app-alerts',
  imports: [GrafanaEmbedComponent, CommonModule, DatePipe],
  templateUrl: './alerts.html',
  styleUrl: './alerts.scss',
  standalone: true
})
export class AlertsComponent implements OnInit {

  private indicationService = inject(VigimareIndicationService);
  private alertService = inject(VigimareAlertService);
  private route = inject(ActivatedRoute);
  keycloakService = inject(KeycloakService);

  private sseSubscription?: Subscription;
  private alertSseSubscription?: Subscription;
  private allAlertsSseSubscription?: Subscription;
  private queryParamsSub?: Subscription;

  indications = signal<VigimareIndication[]>([]);
  loading = signal(false);
  error = signal<string | null>(null);

  currentPage = signal(1);
  pageSize = signal(5);

  globalFilter = signal('');
  sortField = signal<'generatedIn' | 'id'>('id');
  sortDirection = signal<'asc' | 'desc'>('desc');

  deleteModalOpen = signal(false);
  indicationIdToDelete = signal<number | null>(null);

  detailsModalOpen = signal(false);
  selectedIndication = signal<VigimareIndication | null>(null);

  involvedObjectModalOpen = signal(false);
  involvedObjectLoading = signal(false);
  involvedObjectError = signal<string | null>(null);

  selectedInvolvedObjectType = signal<'vessel' | 'object' | null>(null);
  selectedVessel = signal<VigimareVesselResponse | null>(null);
  selectedObject = signal<VigimareObjectResponse | null>(null);

  highlightedAlertId = signal<number | null>(null);

  // All alerts — includes unassigned (e.g. MISP-sourced), updates live via SSE
  allAlerts = signal<VigimareAlert[]>([]);
  allAlertsLoading = signal(false);
  allAlertsError = signal<string | null>(null);
  allAlertsSearch = signal('');
  allAlertsCurrentPage = signal(1);
  allAlertsPageSize = signal(5);
  allAlertsSortField = signal<'generatedIn' | 'id'>('generatedIn');
  allAlertsSortDirection = signal<'asc' | 'desc'>('desc');

  assignedAlerts = signal<VigimareAlert[]>([]);
  assignedAlertsLoading = signal(false);
  assignedAlertsError = signal<string | null>(null);
  assignedAlertsScope = signal<'all' | 'me' | 'organizations'>('all');
  assignedAlertsSearch = signal('');
  assignedAlertsCurrentPage = signal(1);
  assignedAlertsPageSize = signal(5);
  assignedAlertsSortField = signal<'generatedIn' | 'id'>('generatedIn');
  assignedAlertsSortDirection = signal<'asc' | 'desc'>('desc');

  assignedAlertDetailsModalOpen = signal(false);
  selectedAssignedAlert = signal<VigimareAlert | null>(null);
  auditModalOpen = signal(false);
  auditLoading = signal(false);
  auditError = signal<string | null>(null);
  selectedAuditAlert = signal<VigimareAlert | null>(null);
  auditEntries = signal<AlertAuditEntry[]>([]);
  statusModalOpen = signal(false);
  statusSaving = signal(false);
  statusError = signal<string | null>(null);
  selectedStatusAlert = signal<VigimareAlert | null>(null);
  selectedStatus = signal<AlertStatus>('ACKNOWLEDGED');
  commentModalOpen = signal(false);
  commentSaving = signal(false);
  commentError = signal<string | null>(null);
  selectedCommentAlert = signal<VigimareAlert | null>(null);
  commentText = signal('');
  feedbackModalOpen = signal(false);
  feedbackLoading = signal(false);
  feedbackSaving = signal(false);
  feedbackError = signal<string | null>(null);
  selectedFeedbackAlert = signal<VigimareAlert | null>(null);
  feedbackRating = signal(0);
  feedbackMessage = signal('');
  existingFeedback = signal<AlertFeedback | null>(null);
  readonly selectableStatuses: AlertStatus[] = [
    'IN_REVIEW',
    'ESCALATED',
    'RESOLVED',
    'CLOSED',
    'FALSE_POSITIVE',
    'DUPLICATE'
  ];

  hasInvolvedObject(indication: VigimareIndication): boolean {
    return !!indication.involvedVesselIds?.length || !!indication.involvedObjectIds?.length;
  }

  getInvolvedObjectType(indication: VigimareIndication): 'vessel' | 'object' | null {
    if (indication.involvedVesselIds?.length) {
      return 'vessel';
    }

    if (indication.involvedObjectIds?.length) {
      return 'object';
    }

    return null;
  }

  getInvolvedObjectButtonLabel(indication: VigimareIndication): string {
    const type = this.getInvolvedObjectType(indication);

    if (type === 'vessel') {
      return 'Vessel';
    }

    if (type === 'object') {
      return 'Object';
    }

    return '-';
  }

  openInvolvedObjectModal(indication: VigimareIndication): void {
    this.involvedObjectModalOpen.set(true);
    this.involvedObjectLoading.set(true);
    this.involvedObjectError.set(null);
    this.selectedVessel.set(null);
    this.selectedObject.set(null);
    this.selectedInvolvedObjectType.set(null);

    if (indication.involvedVesselIds?.length) {
      const vesselId = indication.involvedVesselIds[0];
      this.selectedInvolvedObjectType.set('vessel');

      this.indicationService.getVesselById(vesselId).subscribe({
        next: (vessel) => {
          this.selectedVessel.set(vessel);
          this.involvedObjectLoading.set(false);
        },
        error: () => {
          this.involvedObjectError.set('Failed to load vessel details.');
          this.involvedObjectLoading.set(false);
        }
      });

      return;
    }

    if (indication.involvedObjectIds?.length) {
      const objectId = indication.involvedObjectIds[0];
      this.selectedInvolvedObjectType.set('object');

      this.indicationService.getObjectById(objectId).subscribe({
        next: (objectDetails) => {
          this.selectedObject.set(objectDetails);
          this.involvedObjectLoading.set(false);
        },
        error: () => {
          this.involvedObjectError.set('Failed to load object details.');
          this.involvedObjectLoading.set(false);
        }
      });

      return;
    }

    this.involvedObjectError.set('No involved object found.');
    this.involvedObjectLoading.set(false);
  }

  closeInvolvedObjectModal(): void {
    this.involvedObjectModalOpen.set(false);
    this.involvedObjectLoading.set(false);
    this.involvedObjectError.set(null);
    this.selectedInvolvedObjectType.set(null);
    this.selectedVessel.set(null);
    this.selectedObject.set(null);
  }

  assignedAlertHasInvolvedObject(alert: VigimareAlert): boolean {
    return !!alert.involvedVesselIds?.length || !!alert.involvedObjectIds?.length;
  }

  getAssignedAlertInvolvedObjectType(alert: VigimareAlert): 'vessel' | 'object' | null {
    if (alert.involvedVesselIds?.length) {
      return 'vessel';
    }

    if (alert.involvedObjectIds?.length) {
      return 'object';
    }

    return null;
  }

  getAssignedAlertInvolvedObjectButtonLabel(alert: VigimareAlert): string {
    const type = this.getAssignedAlertInvolvedObjectType(alert);

    if (type === 'vessel') {
      return 'Vessel';
    }

    if (type === 'object') {
      return 'Object';
    }

    return '-';
  }

  openAssignedAlertInvolvedObjectModal(alert: VigimareAlert): void {
    this.involvedObjectModalOpen.set(true);
    this.involvedObjectLoading.set(true);
    this.involvedObjectError.set(null);
    this.selectedVessel.set(null);
    this.selectedObject.set(null);
    this.selectedInvolvedObjectType.set(null);

    if (alert.involvedVesselIds?.length) {
      const vesselId = alert.involvedVesselIds[0];
      this.selectedInvolvedObjectType.set('vessel');

      this.alertService.getVesselById(vesselId).subscribe({
        next: (vessel) => {
          this.selectedVessel.set(vessel);
          this.involvedObjectLoading.set(false);
        },
        error: () => {
          this.involvedObjectError.set('Failed to load vessel details.');
          this.involvedObjectLoading.set(false);
        }
      });

      return;
    }

    if (alert.involvedObjectIds?.length) {
      const objectId = alert.involvedObjectIds[0];
      this.selectedInvolvedObjectType.set('object');

      this.alertService.getObjectById(objectId).subscribe({
        next: (objectDetails) => {
          this.selectedObject.set(objectDetails);
          this.involvedObjectLoading.set(false);
        },
        error: () => {
          this.involvedObjectError.set('Failed to load object details.');
          this.involvedObjectLoading.set(false);
        }
      });

      return;
    }

    this.involvedObjectError.set('No involved object found.');
    this.involvedObjectLoading.set(false);
  }

  openAssignedAlertDetailsModal(alert: VigimareAlert): void {
    this.selectedAssignedAlert.set(alert);
    this.assignedAlertDetailsModalOpen.set(true);
  }

  closeAssignedAlertDetailsModal(): void {
    this.assignedAlertDetailsModalOpen.set(false);
    this.selectedAssignedAlert.set(null);
  }

  getAssignedAlertTypeLabel(alert: VigimareAlert): string {
    if (alert.anomaly) {
      return 'Anomaly';
    }

    if (alert.intentions?.length) {
      return 'Intention';
    }

    return 'Alert';
  }

  assignedAlertHasDetails(alert: VigimareAlert): boolean {
    return !!alert.anomaly || !!alert.intentions?.length;
  }

  getAssignedAlertType(alert: VigimareAlert): string {
    return alert.anomaly ? 'Anomaly' : 'Intention';
  }

  getAssignedAlertSummary(alert: VigimareAlert): string {
    if (alert.anomaly) {
      return `${alert.anomaly.anomalyType} (${alert.anomaly.anomalyScore})`;
    }

    if (alert.intentions?.length) {
      return alert.intentions
        .map(i => `${i.classification} (${i.probability})`)
        .join(', ');
    }

    return '-';
  }

  getAssignedUsersLabel(alert: VigimareAlert): string {
    return alert.assignedUsers?.length
      ? alert.assignedUsers.map(assignment => assignment.assigneeName).join(', ')
      : '-';
  }

  getAssignedOrganizationsLabel(alert: VigimareAlert): string {
    return alert.assignedOrganizations?.length
      ? alert.assignedOrganizations.map(assignment => assignment.assigneeName).join(', ')
      : '-';
  }

  getLastCommentLabel(alert: VigimareAlert): string {
    return alert.lastComment?.trim() || '-';
  }

  openCommentModal(alert: VigimareAlert): void {
    this.selectedCommentAlert.set(alert);
    this.commentText.set('');
    this.commentError.set(null);
    this.commentModalOpen.set(true);
  }

  closeCommentModal(): void {
    if (this.commentSaving()) {
      return;
    }

    this.commentModalOpen.set(false);
    this.commentError.set(null);
    this.selectedCommentAlert.set(null);
    this.commentText.set('');
  }

  saveComment(): void {
    const alert = this.selectedCommentAlert();
    const text = this.commentText().trim();

    if (!alert || this.commentSaving()) {
      return;
    }

    if (!text) {
      this.commentError.set('Comment text is required.');
      return;
    }

    this.commentSaving.set(true);
    this.commentError.set(null);

    this.alertService.addComment(alert.id, text).subscribe({
      next: () => {
        this.assignedAlerts.update(current =>
          current.map(item => item.id === alert.id ? { ...item, lastComment: text } : item)
        );
        this.commentSaving.set(false);
        this.closeCommentModal();
      },
      error: (err) => {
        console.error('Failed to save comment', err);
        this.commentError.set('Failed to save comment.');
        this.commentSaving.set(false);
      }
    });
  }

  canGiveFeedback(alert: VigimareAlert): boolean {
    return this.isTerminalStatus(alert.status);
  }

  openFeedbackModal(alert: VigimareAlert): void {
    this.selectedFeedbackAlert.set(alert);
    this.feedbackRating.set(0);
    this.feedbackMessage.set('');
    this.existingFeedback.set(null);
    this.feedbackError.set(null);
    this.feedbackModalOpen.set(true);
    this.feedbackLoading.set(true);

    this.alertService.getCurrentUserFeedback(alert.id).subscribe({
      next: (feedback) => {
        this.existingFeedback.set(feedback);
        this.feedbackLoading.set(false);
      },
      error: () => {
        this.existingFeedback.set(null);
        this.feedbackLoading.set(false);
      }
    });
  }

  closeFeedbackModal(): void {
    if (this.feedbackSaving()) {
      return;
    }

    this.feedbackModalOpen.set(false);
    this.feedbackError.set(null);
    this.selectedFeedbackAlert.set(null);
    this.feedbackRating.set(0);
    this.feedbackMessage.set('');
    this.existingFeedback.set(null);
  }

  setFeedbackRating(rating: number): void {
    this.feedbackRating.set(rating);
  }

  saveFeedback(): void {
    const alert = this.selectedFeedbackAlert();
    const message = this.feedbackMessage().trim();

    if (!alert || this.feedbackSaving()) {
      return;
    }

    if (this.feedbackRating() < 1 || this.feedbackRating() > 5) {
      this.feedbackError.set('Select a rating.');
      return;
    }

    if (!message) {
      this.feedbackError.set('Feedback message is required.');
      return;
    }

    this.feedbackSaving.set(true);
    this.feedbackError.set(null);

    this.alertService.addFeedback(alert.id, this.feedbackRating(), message).subscribe({
      next: (feedback) => {
        this.existingFeedback.set(feedback);
        this.feedbackSaving.set(false);
        this.closeFeedbackModal();
      },
      error: (err) => {
        console.error('Failed to save feedback', err);
        this.feedbackError.set('Failed to save feedback.');
        this.feedbackSaving.set(false);
      }
    });
  }

  openAuditModal(alert: VigimareAlert): void {
    this.selectedAuditAlert.set(alert);
    this.auditModalOpen.set(true);
    this.auditLoading.set(true);
    this.auditError.set(null);
    this.auditEntries.set([]);

    this.alertService.getAudit(alert.id).subscribe({
      next: (entries) => {
        this.auditEntries.set(entries);
        this.auditLoading.set(false);
      },
      error: (err) => {
        console.error('Failed to load alert audit', err);
        this.auditError.set('Failed to load alert audit.');
        this.auditLoading.set(false);
      }
    });
  }

  closeAuditModal(): void {
    this.auditModalOpen.set(false);
    this.auditLoading.set(false);
    this.auditError.set(null);
    this.selectedAuditAlert.set(null);
    this.auditEntries.set([]);
  }

  getAuditActionLabel(entry: AlertAuditEntry): string {
    return entry.actionType === 'SHARED' ? 'shared this alert with' : 'removed';
  }

  getAuditTargetLabel(entry: AlertAuditEntry): string {
    return entry.targetType === 'USER' ? 'user' : 'organization';
  }

  getStatusLabel(status: AlertStatus | string | null | undefined): string {
    return (status ?? 'NEW').replaceAll('_', ' ');
  }

  canAcknowledge(alert: VigimareAlert): boolean {
    return (alert.status ?? 'NEW') === 'NEW';
  }

  canChangeStatus(alert: VigimareAlert): boolean {
    return !this.isTerminalStatus(alert.status);
  }

  isTerminalStatus(status: AlertStatus | string | null | undefined): boolean {
    return ['CLOSED', 'FALSE_POSITIVE', 'RESOLVED', 'DUPLICATE'].includes(status ?? 'NEW');
  }

  acknowledgeAlert(alert: VigimareAlert): void {
    this.updateAssignedAlertStatus(alert, 'ACKNOWLEDGED');
  }

  openStatusModal(alert: VigimareAlert): void {
    this.selectedStatusAlert.set(alert);
    this.selectedStatus.set(
      alert.status !== 'NEW' && alert.status !== 'ACKNOWLEDGED'
        ? alert.status
        : 'IN_REVIEW'
    );
    this.statusError.set(null);
    this.statusModalOpen.set(true);
  }

  closeStatusModal(): void {
    if (this.statusSaving()) {
      return;
    }

    this.statusModalOpen.set(false);
    this.statusError.set(null);
    this.selectedStatusAlert.set(null);
    this.selectedStatus.set('IN_REVIEW');
  }

  onStatusSelectionChange(event: Event): void {
    this.selectedStatus.set((event.target as HTMLSelectElement).value as AlertStatus);
  }

  saveStatusChange(): void {
    const alert = this.selectedStatusAlert();

    if (!alert || this.statusSaving()) {
      return;
    }

    this.updateAssignedAlertStatus(alert, this.selectedStatus(), true);
  }

  private updateAssignedAlertStatus(alert: VigimareAlert, status: AlertStatus, closeModal = false): void {
    this.statusSaving.set(true);
    this.statusError.set(null);

    this.alertService.updateStatus(alert.id, status).subscribe({
      next: (updated) => {
        this.assignedAlerts.update(current =>
          current.map(item => item.id === updated.id ? updated : item)
        );
        this.statusSaving.set(false);

        if (closeModal) {
          this.closeStatusModal();
        }
      },
      error: (err) => {
        console.error('Failed to update alert status', err);
        this.statusError.set('Failed to update alert status.');
        this.statusSaving.set(false);
      }
    });
  }

  openDetailsModal(indication: VigimareIndication): void {
    this.selectedIndication.set(indication);
    this.detailsModalOpen.set(true);
  }

  closeDetailsModal(): void {
    this.detailsModalOpen.set(false);
    this.selectedIndication.set(null);
  }

  hasDetails(indication: VigimareIndication): boolean {
    return !!(indication.indicationType || indication.indicationValue || indication.explanation);
  }

  openDeleteModal(id: number): void {
    this.indicationIdToDelete.set(id);
    this.deleteModalOpen.set(true);
  }

  closeDeleteModal(): void {
    this.deleteModalOpen.set(false);
    this.indicationIdToDelete.set(null);
  }

  confirmDelete(): void {
    const id = this.indicationIdToDelete();

    if (id === null) {
      return;
    }

    this.deleteIndication(id);
    this.closeDeleteModal();
  }

  filteredIndications = computed(() => {
    const filter = this.globalFilter().trim().toLowerCase();

    if (!filter) {
      return this.indications();
    }

    return this.indications().filter((indication) => {
      const type = (indication.indicationType ?? '').toLowerCase();
      const legalName = (indication.legalName ?? '').toLowerCase();
      const value = (indication.indicationValue ?? '').toLowerCase();

      return (
        type.includes(filter) ||
        legalName.includes(filter) ||
        value.includes(filter)
      );
    });
  });

  sortedIndications = computed(() => {
    const field = this.sortField();
    const direction = this.sortDirection();

    return [...this.filteredIndications()].sort((a, b) => {
      let comparison = 0;

      if (field === 'id') {
        comparison = (a.id ?? 0) - (b.id ?? 0);
      }

      if (field === 'generatedIn') {
        comparison =
          new Date(a.generatedIn ?? '').getTime() -
          new Date(b.generatedIn ?? '').getTime();
      }

      return direction === 'asc' ? comparison : -comparison;
    });
  });

  sortBy(field: 'generatedIn' | 'id'): void {
    if (this.sortField() === field) {
      this.sortDirection.set(
        this.sortDirection() === 'asc' ? 'desc' : 'asc'
      );
    } else {
      this.sortField.set(field);
      this.sortDirection.set(field === 'generatedIn' ? 'desc' : 'asc');
    }

    this.currentPage.set(1);
  }

  getSortIcon(field: 'generatedIn' | 'id'): string {
    if (this.sortField() !== field) {
      return '\u2195';
    }

    return this.sortDirection() === 'asc' ? '\u2191' : '\u2193';
  }

  onGlobalFilterChange(event: Event): void {
    this.globalFilter.set((event.target as HTMLInputElement).value);
    this.currentPage.set(1);
  }

  clearFilters(): void {
    this.globalFilter.set('');
    this.sortField.set('generatedIn');
    this.sortDirection.set('desc');
    this.currentPage.set(1);
  }

  filteredAssignedAlerts = computed(() => {
    const filter = this.assignedAlertsSearch().trim().toLowerCase();

    if (!filter) {
      return this.assignedAlerts();
    }

    return this.assignedAlerts().filter((alert) => {
      const type = this.getAssignedAlertType(alert).toLowerCase();
      const legalName = (alert.legalName ?? '').toLowerCase();
      const summary = this.getAssignedAlertSummary(alert).toLowerCase();
      const users = this.getAssignedUsersLabel(alert).toLowerCase();
      const organizations = this.getAssignedOrganizationsLabel(alert).toLowerCase();

      return (
        type.includes(filter) ||
        legalName.includes(filter) ||
        summary.includes(filter) ||
        users.includes(filter) ||
        organizations.includes(filter)
      );
    });
  });

  sortedAssignedAlerts = computed(() => {
    const field = this.assignedAlertsSortField();
    const direction = this.assignedAlertsSortDirection();

    return [...this.filteredAssignedAlerts()].sort((a, b) => {
      let comparison = 0;

      if (field === 'id') {
        comparison = (a.id ?? 0) - (b.id ?? 0);
      }

      if (field === 'generatedIn') {
        comparison =
          new Date(a.generatedIn ?? '').getTime() -
          new Date(b.generatedIn ?? '').getTime();
      }

      return direction === 'asc' ? comparison : -comparison;
    });
  });

  assignedAlertsTotalItems = computed(() => this.sortedAssignedAlerts().length);

  assignedAlertsTotalPages = computed(() => {
    const total = Math.ceil(this.assignedAlertsTotalItems() / this.assignedAlertsPageSize());
    return total > 0 ? total : 1;
  });

  paginatedAssignedAlerts = computed(() => {
    const page = this.assignedAlertsCurrentPage();
    const size = this.assignedAlertsPageSize();
    const start = (page - 1) * size;
    const end = start + size;
    return this.sortedAssignedAlerts().slice(start, end);
  });

  assignedAlertsPageNumbers = computed(() =>
    Array.from({ length: this.assignedAlertsTotalPages() }, (_, i) => i + 1)
  );

  setAssignedAlertsScope(scope: 'all' | 'me' | 'organizations'): void {
    this.assignedAlertsScope.set(scope);
    this.assignedAlertsCurrentPage.set(1);
    this.loadAssignedAlerts();
  }

  onAssignedAlertsSearchChange(event: Event): void {
    this.assignedAlertsSearch.set((event.target as HTMLInputElement).value);
    this.assignedAlertsCurrentPage.set(1);
  }

  onAssignedAlertsPageSizeChange(event: Event): void {
    const value = +(event.target as HTMLSelectElement).value;
    this.assignedAlertsPageSize.set(value);
    this.assignedAlertsCurrentPage.set(1);
  }

  clearAssignedAlertsFilters(): void {
    this.assignedAlertsScope.set('all');
    this.assignedAlertsSearch.set('');
    this.assignedAlertsSortField.set('generatedIn');
    this.assignedAlertsSortDirection.set('desc');
    this.assignedAlertsCurrentPage.set(1);
    this.loadAssignedAlerts();
  }

  sortAssignedAlertsBy(field: 'generatedIn' | 'id'): void {
    if (this.assignedAlertsSortField() === field) {
      this.assignedAlertsSortDirection.set(
        this.assignedAlertsSortDirection() === 'asc' ? 'desc' : 'asc'
      );
    } else {
      this.assignedAlertsSortField.set(field);
      this.assignedAlertsSortDirection.set(field === 'generatedIn' ? 'desc' : 'asc');
    }

    this.assignedAlertsCurrentPage.set(1);
  }

  getAssignedAlertsSortIcon(field: 'generatedIn' | 'id'): string {
    if (this.assignedAlertsSortField() !== field) {
      return '\u2195';
    }

    return this.assignedAlertsSortDirection() === 'asc' ? '\u2191' : '\u2193';
  }

  totalItems = computed(() => this.sortedIndications().length);

  totalPages = computed(() => {
    const total = Math.ceil(this.totalItems() / this.pageSize());
    return total > 0 ? total : 1;
  });

  paginatedIndications = computed(() => {
    const page = this.currentPage();
    const size = this.pageSize();
    const start = (page - 1) * size;
    const end = start + size;
    return this.sortedIndications().slice(start, end);
  });

  pageNumbers = computed(() =>
    Array.from({ length: this.totalPages() }, (_, i) => i + 1)
  );

  filteredAllAlerts = computed(() => {
    const filter = this.allAlertsSearch().trim().toLowerCase();
    if (!filter) return this.allAlerts();
    return this.allAlerts().filter(alert => {
      const summary = this.getAssignedAlertSummary(alert).toLowerCase();
      const legalName = (alert.legalName ?? '').toLowerCase();
      const status = (alert.status ?? '').toLowerCase();
      const type = this.getAssignedAlertTypeLabel(alert).toLowerCase();
      return summary.includes(filter) || legalName.includes(filter) || status.includes(filter) || type.includes(filter);
    });
  });

  sortedAllAlerts = computed(() => {
    const field = this.allAlertsSortField();
    const direction = this.allAlertsSortDirection();
    return [...this.filteredAllAlerts()].sort((a, b) => {
      const comparison = field === 'id'
        ? (a.id ?? 0) - (b.id ?? 0)
        : new Date(a.generatedIn ?? '').getTime() - new Date(b.generatedIn ?? '').getTime();
      return direction === 'asc' ? comparison : -comparison;
    });
  });

  allAlertsTotalItems = computed(() => this.sortedAllAlerts().length);
  allAlertsTotalPages = computed(() => Math.max(1, Math.ceil(this.allAlertsTotalItems() / this.allAlertsPageSize())));
  paginatedAllAlerts = computed(() => {
    const start = (this.allAlertsCurrentPage() - 1) * this.allAlertsPageSize();
    return this.sortedAllAlerts().slice(start, start + this.allAlertsPageSize());
  });
  allAlertsPageNumbers = computed(() =>
    Array.from({ length: this.allAlertsTotalPages() }, (_, i) => i + 1)
  );

  ngOnInit(): void {
    this.loadIndications();
    this.listenForIndicationEvents();
    this.loadAllAlerts();
    this.listenForAllAlertEvents();
    this.loadAssignedAlerts();
    this.listenForAssignedAlertEvents();

    this.queryParamsSub = this.route.queryParams.subscribe(params => {
      const alertId = params['alertId'];
      this.highlightedAlertId.set(alertId ? +alertId : null);
      this.scrollToHighlightedAlert();
    });
  }

  listenForIndicationEvents(): void {
    this.sseSubscription = this.indicationService.listenForIndicationEvents().subscribe({
      next: ({ type, indication }) => {
        this.indications.update((current) => {
          const index = current.findIndex(existing => existing.id === indication.id);

          if (type === 'created') {
            if (index !== -1) {
              return current;
            }

            return [indication, ...current];
          }

          if (type === 'updated') {
            if (index === -1) {
              return [indication, ...current];
            }

            const updated = [...current];
            updated[index] = indication;

            return updated;
          }

          return current;
        });
      },
      error: (error) => {
        console.error('SSE indication connection error', error);
      }
    });
  }

  ngOnDestroy(): void {
    this.sseSubscription?.unsubscribe();
    this.alertSseSubscription?.unsubscribe();
    this.allAlertsSseSubscription?.unsubscribe();
    this.queryParamsSub?.unsubscribe();
  }

  loadAllAlerts(): void {
    this.allAlertsLoading.set(true);
    this.allAlertsError.set(null);
    this.alertService.getAll().subscribe({
      next: (data) => {
        this.allAlerts.set(data);
        this.allAlertsLoading.set(false);
      },
      error: () => {
        this.allAlertsError.set('Failed to load alerts.');
        this.allAlertsLoading.set(false);
      }
    });
  }

  listenForAllAlertEvents(): void {
    this.allAlertsSseSubscription = this.alertService.listenForAlertEvents().subscribe({
      next: ({ type, alert }) => {
        this.allAlerts.update(current => {
          if (type === 'created') {
            return current.some(a => a.id === alert.id) ? current : [alert, ...current];
          }
          if (type === 'updated') {
            const idx = current.findIndex(a => a.id === alert.id);
            if (idx === -1) return [alert, ...current];
            const updated = [...current];
            updated[idx] = alert;
            return updated;
          }
          return current;
        });
      },
      error: (error) => console.error('SSE all alerts error', error)
    });
  }

  onAllAlertsSearchChange(event: Event): void {
    this.allAlertsSearch.set((event.target as HTMLInputElement).value);
    this.allAlertsCurrentPage.set(1);
  }

  onAllAlertsPageSizeChange(event: Event): void {
    this.allAlertsPageSize.set(+(event.target as HTMLSelectElement).value);
    this.allAlertsCurrentPage.set(1);
  }

  sortAllAlertsBy(field: 'generatedIn' | 'id'): void {
    if (this.allAlertsSortField() === field) {
      this.allAlertsSortDirection.set(this.allAlertsSortDirection() === 'asc' ? 'desc' : 'asc');
    } else {
      this.allAlertsSortField.set(field);
      this.allAlertsSortDirection.set(field === 'generatedIn' ? 'desc' : 'asc');
    }
    this.allAlertsCurrentPage.set(1);
  }

  getAllAlertsSortIcon(field: 'generatedIn' | 'id'): string {
    if (this.allAlertsSortField() !== field) return '↕';
    return this.allAlertsSortDirection() === 'asc' ? '↑' : '↓';
  }

  previousAllAlertsPage(): void {
    if (this.allAlertsCurrentPage() > 1) this.allAlertsCurrentPage.update(p => p - 1);
  }

  nextAllAlertsPage(): void {
    if (this.allAlertsCurrentPage() < this.allAlertsTotalPages()) this.allAlertsCurrentPage.update(p => p + 1);
  }

  goToAllAlertsPage(page: number): void {
    this.allAlertsCurrentPage.set(page);
  }

  listenForAssignedAlertEvents(): void {
    this.alertSseSubscription = this.alertService.listenForAlertEvents().subscribe({
      next: () => {
        this.loadAssignedAlerts();
      },
      error: (error) => {
        console.error('SSE assigned alert connection error', error);
      }
    });
  }

  loadAssignedAlerts(): void {
    this.assignedAlertsLoading.set(true);
    this.assignedAlertsError.set(null);

    this.alertService.getAssigned(this.assignedAlertsScope()).subscribe({
      next: (data) => {
        this.assignedAlerts.set(data);
        this.assignedAlertsLoading.set(false);
        this.scrollToHighlightedAlert();
      },
      error: (err) => {
        console.error('Failed to load assigned alerts', err);
        this.assignedAlertsError.set('Failed to load assigned alerts.');
        this.assignedAlertsLoading.set(false);
      }
    });
  }

  private scrollToHighlightedAlert(): void {
    const targetId = this.highlightedAlertId();
    if (!targetId) return;
    const index = this.sortedAssignedAlerts().findIndex(a => a.id === targetId);
    if (index === -1) return;
    this.assignedAlertsCurrentPage.set(
      Math.floor(index / this.assignedAlertsPageSize()) + 1
    );
  }

  loadIndications(): void {
    this.loading.set(true);
    this.error.set(null);

    this.indicationService.getAll().subscribe({
      next: (data) => {
        this.indications.set(data);
        this.loading.set(false);
      },
      error: (err) => {
        console.error('Failed to load indications', err);
        this.error.set('Failed to load indications.');
        this.loading.set(false);
      }
    });
  }

  deleteIndication(id: number): void {
    this.indicationService.delete(id).subscribe({
      next: () => {
        const updated = this.indications().filter(i => i.id !== id);
        this.indications.set(updated);

        if (this.currentPage() > this.totalPages()) {
          this.currentPage.set(this.totalPages());
        }
      },
      error: () => {
        this.error.set('Failed to delete indication.');
      }
    });
  }

  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages()) {
      this.currentPage.set(page);
    }
  }

  nextPage(): void {
    if (this.currentPage() < this.totalPages()) {
      this.currentPage.update(page => page + 1);
    }
  }

  previousPage(): void {
    if (this.currentPage() > 1) {
      this.currentPage.update(page => page - 1);
    }
  }

  onPageSizeChange(event: Event): void {
    const value = +(event.target as HTMLSelectElement).value;
    this.pageSize.set(value);
    this.currentPage.set(1);
  }

  goToAssignedAlertsPage(page: number): void {
    if (page >= 1 && page <= this.assignedAlertsTotalPages()) {
      this.assignedAlertsCurrentPage.set(page);
    }
  }

  nextAssignedAlertsPage(): void {
    if (this.assignedAlertsCurrentPage() < this.assignedAlertsTotalPages()) {
      this.assignedAlertsCurrentPage.update(page => page + 1);
    }
  }

  previousAssignedAlertsPage(): void {
    if (this.assignedAlertsCurrentPage() > 1) {
      this.assignedAlertsCurrentPage.update(page => page - 1);
    }
  }

  hasAnyIndicationAction(): boolean {
    return this.keycloakService.canDeleteIndication();
  }

  grafanaAlerts =
    'https://rd.vigimare.simavi.ro/grafana/d/vigimare-alert-monitoring/vigimare-alert-threat-monitoring' +
    '?orgId=1' +
    '&kiosk' +
    '&from=now-6h' +
    '&to=now' +
    '&refresh=30s';
  protected readonly Math = Math;
}
